import type { CaseStatus, FileKind, Role, Severity } from '../../shared/types'

export interface SessionUser {
  id: string
  company_id: string | null
  email: string
  name: string
  role: Role
}

export interface CompanySummary {
  id: string
  name: string
  slug: string
  form_token: string
}

export interface CaseListItem {
  id: string
  case_number: string
  address: string | null
  incident_date: string | null
  opened_at: string
  closed_at: string | null
  severity: Severity | null
  status: CaseStatus
  description: string | null
}

export interface CaseDetailRow {
  id: string
  case_number: string
  status: CaseStatus
  technician_name: string | null
  technician_phone: string | null
  supervisor_name: string | null
  supervisor_phone: string | null
  customer_name: string | null
  customer_phone: string | null
  customer_email: string | null
  address: string | null
  incident_date: string | null
  description: string | null
  severity: Severity | null
  assigned_subcontractor_id: string | null
  opened_at: string
  closed_at: string | null
  updated_at: string
}

export interface SubcontractorRef {
  id: string
  name: string
  contact_person: string | null
  phone: string | null
  email: string | null
  specialty: string | null
}

// A subcontractor as shown in the management list: identity + specialty plus a
// flattened view of its main contact (the one displayed in the list).
export interface SubcontractorListItem {
  id: string
  name: string
  specialty: string | null
  main_contact_name: string | null
  main_contact_title: string | null
  main_contact_phone: string | null
  main_contact_email: string | null
}

export interface SubContact {
  id: string
  name: string
  title: string | null
  phone: string | null
  email: string | null
  is_main: number
}

export interface SubContactInput {
  name: string
  title?: string | null
  phone?: string | null
  email?: string | null
  is_main?: boolean
}

export interface TechnicianRef {
  id: string
  name: string
  phone: string | null
  email: string | null
  active: number
  supervisor_id: string | null
  supervisor_name: string | null
  supervisor_phone: string | null
  branch_id: string | null
  branch_name: string | null
}

// A branch (location) within a company.
export interface BranchRef {
  id: string
  name: string
  active: number
  cases: number
}
// What the public form needs: the tech plus their assigned supervisor, so both
// sets of contact details can pre-fill.
export interface TechnicianOption {
  id: string
  name: string
  phone: string | null
  supervisor_name: string | null
  supervisor_phone: string | null
}
export interface SupervisorRef {
  id: string
  name: string
  phone: string | null
  email: string | null
  active: number
}
// Branch as offered on the public form.
export interface BranchOption {
  id: string
  name: string
}

export interface SubcontractorInput {
  name: string
  contact_person?: string | null
  phone?: string | null
  email?: string | null
  specialty?: string | null
}

export interface CasePhotoRef {
  id: string
  uploaded_at: string
  is_pdf: number
}
export interface CaseFileRef {
  id: string
  kind: FileKind
  amount: number | null
  uploaded_at: string
}
export interface CaseCommentRef {
  id: string
  body: string
  author: string | null
  created_at: string
}

// Editable case fields (customer / technician / damage info).
export interface CaseEdit {
  customer_name: string | null
  customer_phone: string | null
  customer_email: string | null
  address: string | null
  technician_name: string | null
  technician_phone: string | null
  supervisor_name: string | null
  supervisor_phone: string | null
  incident_date: string | null
  description: string | null
  severity: Severity | null
}

export interface CaseDetailResponse {
  case: CaseDetailRow
  photos: CasePhotoRef[]
  files: CaseFileRef[]
  comments: CaseCommentRef[]
  subcontractors: SubcontractorRef[]
  cost_total: number
  branch: { id: string; name: string } | null
}

export interface TeamUser {
  id: string
  email: string
  name: string
  role: Role
  notify_on_new_damage: number
  created_at: string
  branch_ids: string[]
}

export interface OnboardingStatus {
  done: boolean
  formToken: string
  caseCount: number
  userCount: number
  technicianCount: number
}

export type DashboardRange = 'week' | 'month' | 'year' | 'custom'
export interface DashboardData {
  range: DashboardRange
  from: string
  to: string
  statusCounts: { open: number; in_progress: number; closed: number }
  totalDamages: number
  totalCost: number
  timeseries: { bucket: string; count: number }[]
  topTechnicians: { name: string; c: number; id: string | null }[]
  topSubcontractors: { name: string; c: number; id: string }[]
  recentActivity: { actor: string | null; action: string; created_at: string }[]
}

export interface ProfileStats {
  total: number
  open: number
  in_progress: number
  closed: number
}
export interface TechnicianProfile {
  technician: TechnicianRef
  stats: ProfileStats
  cases: CaseListItem[]
}
export interface SubcontractorProfile {
  subcontractor: SubcontractorRef
  contacts: SubContact[]
  stats: ProfileStats & { totalCost: number }
  cases: CaseListItem[]
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code)
  }
}

// Active company selection for a super_admin viewing a specific company. Stored
// in localStorage and sent as X-Company-Id so the API scopes to it. Company
// users don't set this — the server uses their own company_id.
const ACTIVE_KEY = 'rd_active_company'
let activeCompanyId: string | null = readActive()?.id ?? null

function readActive(): { id: string; name: string } | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY)
    return raw ? (JSON.parse(raw) as { id: string; name: string }) : null
  } catch {
    return null
  }
}
export function getActiveCompany(): { id: string; name: string } | null {
  return readActive()
}
export function setActiveCompany(company: { id: string; name: string } | null): void {
  activeCompanyId = company?.id ?? null
  try {
    if (company) localStorage.setItem(ACTIVE_KEY, JSON.stringify(company))
    else localStorage.removeItem(ACTIVE_KEY)
  } catch {
    /* storage unavailable */
  }
}

export function scopeHeaders(): Record<string, string> {
  return activeCompanyId ? { 'X-Company-Id': activeCompanyId } : {}
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...scopeHeaders(), ...init?.headers },
  })
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) throw new ApiError(res.status, (data.error as string) ?? 'request_failed')
  return data as T
}

function rangeQuery(range?: string, from?: string, to?: string): string {
  const sp = new URLSearchParams()
  if (range === 'custom' && from && to) {
    sp.set('from', from)
    sp.set('to', to)
  } else if (range && range !== 'all') {
    sp.set('range', range)
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const api = {
  me: () => req<{ user: SessionUser; company: CompanySummary | null }>('/auth/me'),
  login: (email: string, password: string) =>
    req<{ user: SessionUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (payload: { companyName: string; name: string; email: string; password: string }) =>
    req<{ user: SessionUser }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getOnboarding: () => req<OnboardingStatus>('/onboarding'),
  dismissOnboarding: () => req<{ ok: true }>('/onboarding/dismiss', { method: 'POST' }),
  logout: () => req<{ ok: true }>('/auth/logout', { method: 'POST' }),
  changePassword: (current_password: string, new_password: string) =>
    req<{ ok: true }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),

  listCompanies: () =>
    req<{
      companies: Array<CompanySummary & { created_at: string; user_count: number; case_count: number }>
    }>('/admin/companies'),
  createCompany: (payload: {
    companyName: string
    adminName: string
    adminEmail: string
    adminPassword: string
  }) => req<{ company: CompanySummary; admin: SessionUser }>('/admin/companies', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

  // Cases (company-scoped)
  // status 'all' omits the status filter. openedFrom/openedTo bound the reported
  // date (opened_at) — what the dashboard stat tiles count.
  listCases: (
    status: CaseStatus | 'all',
    sort: 'asc' | 'desc',
    openedFrom?: string,
    openedTo?: string,
    branch?: string,
  ) => {
    const sp = new URLSearchParams({ sort })
    if (status !== 'all') sp.set('status', status)
    if (openedFrom) sp.set('openedFrom', openedFrom)
    if (openedTo) sp.set('openedTo', openedTo)
    if (branch) sp.set('branch', branch)
    return req<{ cases: CaseListItem[] }>(`/cases?${sp.toString()}`)
  },
  getCase: (id: string) => req<CaseDetailResponse>(`/cases/${id}`),
  deleteCase: (id: string) => req<{ ok: true }>(`/cases/${id}`, { method: 'DELETE' }),
  mergeCase: (id: string, target: string) =>
    req<{ ok: true; target_id: string; target_case_number: string }>(`/cases/${id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ target }),
    }),
  updateCase: (
    id: string,
    patch: { status?: CaseStatus; branch_id?: string | null } & Partial<CaseEdit>,
  ) => req<{ ok: true }>(`/cases/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  addCasePhotos: async (id: string, data: FormData) => {
    const res = await fetch(`/api/cases/${id}/photos`, {
      method: 'POST',
      credentials: 'include',
      headers: scopeHeaders(),
      body: data,
    })
    const b = (await res.json().catch(() => ({}))) as Record<string, unknown>
    if (!res.ok) throw new ApiError(res.status, (b.error as string) ?? 'request_failed')
    return b as { added: number }
  },
  addCaseSubcontractor: (id: string, subcontractor_id: string) =>
    req<{ ok: true }>(`/cases/${id}/subcontractors`, {
      method: 'POST',
      body: JSON.stringify({ subcontractor_id }),
    }),
  removeCaseSubcontractor: (id: string, subId: string) =>
    req<{ ok: true }>(`/cases/${id}/subcontractors/${subId}`, { method: 'DELETE' }),
  addComment: (id: string, body: string) =>
    req<{ id: string; body: string; author: string }>(`/cases/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  uploadCaseFile: async (id: string, data: FormData) => {
    const res = await fetch(`/api/cases/${id}/files`, {
      method: 'POST',
      credentials: 'include',
      headers: scopeHeaders(),
      body: data,
    })
    const b = (await res.json().catch(() => ({}))) as Record<string, unknown>
    if (!res.ok) throw new ApiError(res.status, (b.error as string) ?? 'request_failed')
    return b as { id: string; kind: FileKind; amount: number | null }
  },
  photoUrl: (caseId: string, photoId: string) => `/api/cases/${caseId}/photos/${photoId}`,
  fileUrl: (caseId: string, fileId: string) => `/api/cases/${caseId}/files/${fileId}`,
  deleteCasePhoto: (caseId: string, photoId: string) =>
    req<{ ok: true }>(`/cases/${caseId}/photos/${photoId}`, { method: 'DELETE' }),
  deleteCaseFile: (caseId: string, fileId: string) =>
    req<{ ok: true }>(`/cases/${caseId}/files/${fileId}`, { method: 'DELETE' }),

  searchCases: (params: {
    q?: string
    status?: CaseStatus | ''
    from?: string
    to?: string
    sort?: 'asc' | 'desc'
    branch?: string
  }) => {
    const sp = new URLSearchParams()
    if (params.q) sp.set('q', params.q)
    if (params.status) sp.set('status', params.status)
    if (params.from) sp.set('from', params.from)
    if (params.to) sp.set('to', params.to)
    if (params.branch) sp.set('branch', params.branch)
    sp.set('sort', params.sort ?? 'desc')
    return req<{ cases: CaseListItem[] }>(`/cases?${sp.toString()}`)
  },

  getDashboard: (range: DashboardRange, from?: string, to?: string, branch?: string) => {
    const sp = new URLSearchParams()
    if (range === 'custom' && from && to) {
      sp.set('from', from)
      sp.set('to', to)
    } else {
      sp.set('range', range)
    }
    if (branch) sp.set('branch', branch)
    return req<DashboardData>(`/dashboard?${sp.toString()}`)
  },

  // Branches (locations)
  listBranches: () => req<{ branches: BranchRef[] }>('/branches'),
  createBranch: (name: string) =>
    req<{ id: string }>('/branches', { method: 'POST', body: JSON.stringify({ name }) }),
  updateBranch: (id: string, payload: Partial<{ name: string; active: boolean }>) =>
    req<{ ok: true }>(`/branches/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteBranch: (id: string) =>
    req<{ ok: true; archived: boolean }>(`/branches/${id}`, { method: 'DELETE' }),

  getTechnicianProfile: (id: string, range?: string, from?: string, to?: string) =>
    req<TechnicianProfile>(`/profiles/technician/${id}${rangeQuery(range, from, to)}`),
  getSubcontractorProfile: (id: string, range?: string, from?: string, to?: string) =>
    req<SubcontractorProfile>(`/profiles/subcontractor/${id}${rangeQuery(range, from, to)}`),

  // Team / users
  listUsers: () => req<{ users: TeamUser[] }>('/users'),
  createUser: (payload: {
    name: string
    email: string
    password: string
    role: Role
    notify_on_new_damage: boolean
    branch_ids?: string[]
  }) => req<{ id: string }>('/users', { method: 'POST', body: JSON.stringify(payload) }),
  updateUser: (
    id: string,
    payload: Partial<{
      name: string
      role: Role
      notify_on_new_damage: boolean
      password: string
      branch_ids: string[]
    }>,
  ) => req<{ ok: true }>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteUser: (id: string) => req<{ ok: true }>(`/users/${id}`, { method: 'DELETE' }),

  // xlsx import (rows already parsed + mapped client-side)
  importCases: (rows: Record<string, string>[]) =>
    req<{ created: number }>('/import', { method: 'POST', body: JSON.stringify({ rows }) }),

  // Technicians (roster)
  listTechnicians: () => req<{ technicians: TechnicianRef[] }>('/technicians'),
  createTechnician: (payload: {
    name: string
    phone?: string | null
    email?: string | null
    supervisor_id?: string | null
    branch_id?: string | null
  }) => req<{ id: string }>('/technicians', { method: 'POST', body: JSON.stringify(payload) }),
  updateTechnician: (
    id: string,
    payload: Partial<{
      name: string
      phone: string | null
      email: string | null
      active: boolean
      supervisor_id: string | null
      branch_id: string | null
    }>,
  ) => req<{ ok: true }>(`/technicians/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteTechnician: (id: string) => req<{ ok: true }>(`/technicians/${id}`, { method: 'DELETE' }),

  // Supervisors (roster) — technicians are assigned to one
  listSupervisors: () => req<{ supervisors: SupervisorRef[] }>('/supervisors'),
  createSupervisor: (payload: { name: string; phone?: string | null; email?: string | null }) =>
    req<{ id: string }>('/supervisors', { method: 'POST', body: JSON.stringify(payload) }),
  updateSupervisor: (
    id: string,
    payload: Partial<{ name: string; phone: string | null; email: string | null; active: boolean }>,
  ) => req<{ ok: true }>(`/supervisors/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteSupervisor: (id: string) => req<{ ok: true }>(`/supervisors/${id}`, { method: 'DELETE' }),

  listSubcontractors: () => req<{ subcontractors: SubcontractorListItem[] }>('/subcontractors'),
  createSubcontractor: (payload: SubcontractorInput) =>
    req<{ id: string }>('/subcontractors', { method: 'POST', body: JSON.stringify(payload) }),
  updateSubcontractor: (id: string, payload: SubcontractorInput) =>
    req<{ ok: true }>(`/subcontractors/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteSubcontractor: (id: string) =>
    req<{ ok: true }>(`/subcontractors/${id}`, { method: 'DELETE' }),

  // Subcontractor contacts (multiple per sub, one flagged main).
  listSubContacts: (subId: string) =>
    req<{ contacts: SubContact[] }>(`/subcontractors/${subId}/contacts`),
  addSubContact: (subId: string, payload: SubContactInput) =>
    req<{ id: string }>(`/subcontractors/${subId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateSubContact: (subId: string, contactId: string, payload: Partial<SubContactInput>) =>
    req<{ ok: true }>(`/subcontractors/${subId}/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteSubContact: (subId: string, contactId: string) =>
    req<{ ok: true }>(`/subcontractors/${subId}/contacts/${contactId}`, { method: 'DELETE' }),

  // Public damage form (no auth). Multipart submit, so we bypass the JSON helper.
  getPublicForm: (token: string) =>
    req<{ company: { name: string }; technicians: TechnicianOption[]; branches: BranchOption[] }>(
      `/f/${token}`,
    ),
  getPersonalForm: (token: string, techId: string) =>
    req<{
      company: { name: string }
      technician: TechnicianOption & { branch_id: string | null; branch_name: string | null }
      branches: BranchOption[]
    }>(`/f/${token}/t/${techId}`),
  submitPublicForm: async (token: string, data: FormData) => {
    const res = await fetch(`/api/f/${token}`, { method: 'POST', body: data })
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
    if (!res.ok) throw new ApiError(res.status, (body.error as string) ?? 'request_failed')
    return body as { ok: true; case_number: string }
  },
}
